import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { finalize, lastValueFrom } from 'rxjs';

import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { EmpresasService } from '../../../../services/empresa/empresas.service';
import { GruposService } from '../../../../services/grupo/grupos.service';
import { getPaginatorIntl } from '../../../../shared/mat-paginator-intl';

interface EmpresaTable {
  id: number;
  nomeFantasia: string;
  razaoSocial: string;
  cnpj: string;
  estado: string;
  cidade: string;
  selecionado?: boolean;
  bloqueado?: boolean;
}

@Component({
  selector: 'app-selecionar-empresas-grupo',
  imports: [
    MatProgressSpinnerModule,
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  templateUrl: './selecionar-empresas-grupo.component.html',
  styleUrl: './selecionar-empresas-grupo.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: MatPaginatorIntl, useFactory: getPaginatorIntl }],
})
export class SelecionarEmpresasGrupoComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  public isLoading = false;
  mustSaveSelected = false;
  empresas = new MatTableDataSource<EmpresaTable>();
  listaCompleta: EmpresaTable[] = [];

  colunasTabela = [
    { id: 'nomeFantasia', header: 'Nome', propriedade: 'nomeFantasia' },
    { id: 'cnpj', header: 'CNPJ', propriedade: 'cnpj' },
    { id: 'estado', header: 'Estado', propriedade: 'estado' },
    { id: 'cidade', header: 'Cidade', propriedade: 'cidade' },
  ];

  opcoesfiltro = [
    { id: 'nomeFantasia', nome: 'Nome' },
    { id: 'cnpj', nome: 'CNPJ' },
    { id: 'estado', nome: 'Estado' },
    { id: 'cidade', nome: 'Cidade' }
  ];
  filtroSelecionado: string = 'nomeFantasia';
  placeholderBusca: string = 'Buscar por nome';
  empresasSelecionadas: EmpresaTable[] = [];
  empresasSelecionadasLocal: EmpresaTable[] = [];
  empresasBloqueadas: EmpresaTable[] = [];

  pageSize = 10;
  pageSizeOptions = [5, 10, 15];
  total = 0;
  inicio = 1;
  fim = 10;


  get displayedColumns(): string[] {
    return ['selecionar', ...this.colunasTabela.map((coluna) => coluna.id)];
  }

  constructor(
    private empresasService: EmpresasService,
    private gruposService: GruposService,
    public dialogRef: MatDialogRef<SelecionarEmpresasGrupoComponent>,
    private snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  empresaSelecionado = (empresa: EmpresaTable): boolean => {
    const empresaSelecionadaManualmente =
      this.empresasSelecionadas.some((e) => e.id === empresa.id) ||
      this.empresasSelecionadasLocal.some((e) => e.id === empresa.id);

    return empresaSelecionadaManualmente;
  };

  empresaBloqueado = (empresa: EmpresaTable): boolean => {
    return false;
  };

  async ngOnInit(): Promise<void> {
    this.empresasSelecionadasLocal = [...(this.data.empresas || [])];
    this.isLoading = true;
    this.updatePlaceholder();

    await this.buscarEmpresasAPI();
    this.configurarFiltros();

    this.empresas.data = this.empresas.data.map((e) => ({
      ...e,
      selecionado: this.empresaSelecionado(e),
      bloqueado: this.empresaBloqueado(e),
    }));
  }

  ngAfterViewInit(): void {
    this.empresas.paginator = this.paginator;
    this.empresas.sort = this.sort;
  }

  async buscarEmpresasAPI() {
    try {
      const empresas =
        await this.empresasService.findEmpresasSemGrupo();
      this.isLoading = false;

      this.listaCompleta = empresas.map((e: any) => ({
        ...e,
        selecionado: this.empresaSelecionado(e),
        bloqueado: this.empresaBloqueado(e),
      }));

      this.empresasBloqueadas = this.listaCompleta
        .filter((e) => e.bloqueado)
        .map((e) => ({ ...e }));

      this.empresas.data = [...this.listaCompleta];

      this.total = this.empresas.data.length;
      this.atualizarContagem(0, this.pageSize);
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      this.snackbarService.error('Erro ao carregar empresas');
      this.empresas.data = [];
      this.isLoading = false;
    }
  }

  onFiltroChange() {
    this.updatePlaceholder();
  }

  private updatePlaceholder() {
    const filtroAtivo = this.opcoesfiltro.find(f => f.id === this.filtroSelecionado);
    this.placeholderBusca = filtroAtivo ? `Buscar por ${filtroAtivo.nome.toLowerCase()}` : 'Buscar';
  }

  private configurarFiltros() {
    this.empresas.filterPredicate = (
      data: EmpresaTable,
      filter: string
    ): boolean => {
      const termo = filter.trim().toLowerCase();
      
      switch (this.filtroSelecionado) {
        case 'nomeFantasia':
          return (data.nomeFantasia || '').toLowerCase().includes(termo);
        case 'cnpj':
          return (data.cnpj || '').toLowerCase().includes(termo);
        case 'estado':
          return (data.estado || '').toLowerCase().includes(termo);
        case 'cidade':
          return (data.cidade || '').toLowerCase().includes(termo);
        default:
          return (
            (data.nomeFantasia || '').toLowerCase().includes(termo) ||
            (data.cnpj || '').toLowerCase().includes(termo)
          );
      }
    };
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.atualizarContagem(event.pageIndex, event.pageSize);
  }

  atualizarContagem(pageIndex: number, pageSize: number) {
    this.inicio = pageIndex * pageSize + 1;
    this.fim = Math.min((pageIndex + 1) * pageSize, this.total);
  }

  updateFieldText(event: Event) {
    this.configurarFiltros();
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.empresas.filter = query;
  }

  alternarEmpresaSelecionada(empresa: EmpresaTable, isChecked: boolean): void {
    if (this.empresaBloqueado(empresa)) return;

    empresa.selecionado = isChecked;

    if (isChecked) {
      this.empresasSelecionadasLocal.push(empresa);
    } else {
      this.empresasSelecionadasLocal = this.empresasSelecionadasLocal.filter(
        (e) => e.id !== empresa.id
      );
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  formatarCnpj(cnpj: string): string {
    if (!cnpj) return '';
    
    // Remove tudo que não é dígito
    const apenasNumeros = cnpj.replace(/\D/g, '');
    
    // Aplica a máscara XX.XXX.XXX/XXXX-XX
    if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    
    return cnpj; // Retorna o original se não tiver 14 dígitos
  }

  async onConfirm() {
    const empresasConfirmadas = this.empresasSelecionadasLocal.filter(
      (e) => !this.empresaBloqueado(e as EmpresaTable)
    );

    if (empresasConfirmadas.length === 0) {
      this.snackbarService.error('Selecione pelo menos uma empresa');
      return;
    }

    try {
      this.isLoading = true;

      const empresasIds = empresasConfirmadas.map(e => e.id);
      await this.gruposService.vincularEmpresasAoGrupo(this.data.idGrupo, empresasIds);

      this.snackbarService.success(`${empresasConfirmadas.length} empresa(s) vinculada(s) ao grupo`);
      this.dialogRef.close(empresasConfirmadas);
    } catch (error) {
      console.error('Erro ao vincular empresas ao grupo:', error);
      this.snackbarService.error('Erro ao vincular empresas ao grupo');
    } finally {
      this.isLoading = false;
    }
  }
}